// src/features/export/exportFormats/pngExport.ts
import { toPng } from 'html-to-image';
import { Model } from '../../../types';
import {
    ExportHandler,
    ExportResult,
    ExportConfig,
    ExportProgressCallback,
    ExportQuality,
    toExportQuality,
} from '../exportTypes';
import {
    generateFilename,
    createExportMetadata,
} from '../exportUtils';
import { beginVisualExport } from '../exportPreparation';

const MAX_CANVAS_DIMENSION = 8192; // Conservative browser limit
const MAX_CANVAS_AREA = 16777216;  // 4096 * 4096 pixels

export class PNGExportHandler implements ExportHandler {
    public readonly format = 'png' as const;

    /**
     * Detect current theme (dark/light mode)
     */
    private detectCurrentTheme(): 'dark' | 'light' {
        if (document.documentElement.classList.contains('dark')) return 'dark';
        if (document.body.classList.contains('dark')) return 'dark';
        const muiTheme = document.querySelector('[data-mui-color-scheme]');
        if (muiTheme?.getAttribute('data-mui-color-scheme') === 'dark') return 'dark';
        if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
        return 'light';
    }

    /**
     * Get theme-appropriate background color
     */
    private getThemeBackgroundColor(userColor?: string, transparent?: boolean): string | undefined {
        if (transparent) return undefined;
        if (userColor) return userColor;

        // Auto-detect theme and return appropriate color
        const theme = this.detectCurrentTheme();
        return theme === 'dark' ? '#1e293b' : '#ffffff';
    }

    /**
     * Check if dimensions are within browser canvas limits
     */
    private checkCanvasLimits(width: number, height: number, scale: number): {
        exceedsLimit: boolean;
        suggestedScale: number;
        reason?: string;
    } {
        const scaledWidth = width * scale;
        const scaledHeight = height * scale;
        const area = scaledWidth * scaledHeight;

        // Check dimension limits
        if (scaledWidth > MAX_CANVAS_DIMENSION || scaledHeight > MAX_CANVAS_DIMENSION) {
            const maxScale = Math.floor(
                Math.min(
                    MAX_CANVAS_DIMENSION / width,
                    MAX_CANVAS_DIMENSION / height
                )
            );
            return {
                exceedsLimit: true,
                suggestedScale: Math.max(1, maxScale),
                reason: `Canvas dimensions ${scaledWidth}x${scaledHeight} exceed browser limit of ${MAX_CANVAS_DIMENSION}px per side`
            };
        }

        // Check area limits
        if (area > MAX_CANVAS_AREA) {
            const maxScale = Math.floor(Math.sqrt(MAX_CANVAS_AREA / (width * height)));
            return {
                exceedsLimit: true,
                suggestedScale: Math.max(1, maxScale),
                reason: `Canvas area ${area} exceeds browser limit of ${MAX_CANVAS_AREA} pixels`
            };
        }

        return {
            exceedsLimit: false,
            suggestedScale: scale
        };
    }

    async export(
        element: HTMLElement,
        model: Model,
        config: ExportConfig,
        onProgress?: ExportProgressCallback
    ): Promise<ExportResult> {
        try {
            onProgress?.(0, 'Starting PNG export...');

            const quality: ExportQuality = config.quality ? toExportQuality(config.quality) : 2;
            let scale = quality;

            const sizeCheck = this.checkCanvasLimits(
                element.offsetWidth,
                element.offsetHeight,
                scale
            );

            if (sizeCheck.exceedsLimit) {
                console.warn('⚠️ ' + sizeCheck.reason);
                console.warn(`⚠️ Reducing quality from ${scale}x to ${sizeCheck.suggestedScale}x`);

                // Downgrade to suggested scale
                scale = sizeCheck.suggestedScale as ExportQuality;

                // If we're still stuck at 1x and user requested 4x, that's a real problem
                if (scale === 1 && quality === 4) {
                    return {
                        success: false,
                        filename: '',
                        error: new Error(
                            `Cannot export at ${quality}x resolution. ` +
                            `Canvas would be ${element.offsetWidth * quality}x${element.offsetHeight * quality}px, ` +
                            `which exceeds browser limits. Maximum quality available: ${sizeCheck.suggestedScale}x. ` +
                            `Try zooming out or exporting a smaller portion of the diagram.`
                        )
                    };
                }
            }

            onProgress?.(20, 'Preparing canvas...');

            const backgroundColor = this.getThemeBackgroundColor(
                config.backgroundColor,
                config.transparent
            );

            // Quick Export bypasses ExportDialog, so activate the same export-only
            // label styles here for every PNG entry point.
            const restoreExportState = beginVisualExport(element, backgroundColor);
            const removeHideStyles = this.injectHideStyles();
            const cleanup = () => {
                removeHideStyles();
                restoreExportState();
            };

            // Wait for React to re-render and styles to apply
            await new Promise(resolve => setTimeout(resolve, 500));

            onProgress?.(50, 'Capturing diagram...');

            // Suppress console warnings
            const originalError = console.error;
            const suppressedPatterns = [
                'chrome-extension://',
                'fabric-icons',
                'leelawadeeui',
                'font',
                'Status:0',
                'Status:404',
                'ERR_FILE_NOT_FOUND'
            ];

            console.error = (...args: any[]) => {
                const msg = args.join(' ').toString();
                const shouldSuppress = suppressedPatterns.some(pattern =>
                    msg.toLowerCase().includes(pattern.toLowerCase())
                );
                if (!shouldSuppress) {
                    originalError(...args);
                }
            };

            try {
                // Get background color with theme auto-detection
                const dataUrl = await toPng(element, {
                    cacheBust: true,
                    pixelRatio: scale,
                    width: element.offsetWidth,
                    height: element.offsetHeight,
                    backgroundColor: backgroundColor,
                    // ✨ CRITICAL: Filter function to exclude UI elements
                    filter: (node: HTMLElement) => {
                        // Skip non-element nodes
                        if (!node.classList) return true;

                        // Exclude grid if includeGrid is false
                        if (!config.includeGrid && node.getAttribute('data-grid') === 'true') {
                            return false;
                        }

                        // Exclude elements with these classes
                        const excludeClasses = [
                            'MuiBackdrop',
                            'MuiDialog',
                            'transform-control',
                            'selection-box',
                            'resize-handle',
                            'anchor-circle',
                            'control-point',
                            'connector-handle'
                        ];

                        for (const cls of excludeClasses) {
                            if (node.classList.contains(cls)) {
                                return false;
                            }
                        }

                        // Exclude by data attributes
                        if (node.getAttribute('data-element') === 'transform-control') return false;
                        if (node.getAttribute('data-element') === 'cursor') return false;
                        if (node.getAttribute('data-interaction-layer') === 'true') return false;

                        // Exclude hidden elements
                        if (node.style.display === 'none') return false;
                        if (node.style.visibility === 'hidden') return false;
                        if (node.style.opacity === '0') return false;

                        return true;
                    },
                    // Style overrides to ensure clean export
                    style: {
                        // Remove any inherited transform
                        transform: 'none'
                    }
                });

                console.error = originalError;
                cleanup();

                onProgress?.(70, 'Processing image...');

                // Convert data URL to blob
                const response = await fetch(dataUrl);
                const blob = await response.blob();

                onProgress?.(90, 'Finalizing...');

                // Generate filename
                const filename = config.filename || generateFilename(model.title || 'flowi-diagram', 'png');
                const metadata = createExportMetadata(
                    config,
                    element.offsetWidth,
                    element.offsetHeight,
                    model.title
                );

                onProgress?.(100, 'Export complete!');

                return {
                    success: true,
                    data: blob,
                    filename,
                    format: 'png',
                    metadata,
                };
            } finally {
                console.error = originalError;
                cleanup();
            }
        } catch (error) {
            console.error('❌ PNG export failed:', error);
            return {
                success: false,
                filename: '',
                error: error as Error,
            };
        }
    }

    /**
     * Inject CSS to hide UI elements during export while preserving label borders
     */
    private injectHideStyles(): () => void {

        //const bgColor = this.detectCurrentTheme() === 'dark' ? '#1e293b' : '#ffffff';
        const styleEl = document.createElement('style');
        styleEl.id = 'flowi-export-cleanup';
        styleEl.textContent = `
            /* ============ HIDE TRANSFORM CONTROLS & UI ELEMENTS ============ */
            [data-element="transform-control"],
            [data-element="cursor"],
            [data-interaction-layer="true"],
            .MuiBackdrop-root,
            .MuiDialog-root,
            .transform-control,
            .selection-box,
            .resize-handle,
            .anchor-circle,
            .control-point,
            .connector-handle {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
            }

            /* ============ REMOVE BORDERS FROM ICONS & IMAGES (NOT LABELS) ============ */
            img:not([data-flowi-label] img):not(.flowi-label img) {
                border: none !important;
                outline: none !important;
                box-shadow: none !important;
                box-sizing: content-box !important;
            }

            /* ============ TARGET SVG ELEMENTS (NOT LABELS) ============ */
            svg:not([data-flowi-label] svg):not(.flowi-label svg) {
                border: none !important;
                outline: none !important;
            }

            svg:not([data-flowi-label] svg) rect[stroke],
            svg:not([data-flowi-label] svg) circle[stroke],
            svg:not([data-flowi-label] svg) path[stroke] {
                stroke: none !important;
            }

            /* Remove selection rectangles (white with gray borders) */
            svg rect[fill="#ffffff"][stroke="rgb(189, 189, 189)"],
            svg rect[fill="rgb(255, 255, 255)"][stroke="rgb(189, 189, 189)"],
            svg rect[fill="#fff"][stroke="#bdbdbd"],
            svg rect[stroke="rgb(189, 189, 189)"],
            svg rect[stroke="rgb(158, 158, 158)"],
            svg rect[stroke="#bdbdbd"],
            svg rect[stroke="#9e9e9e"] {
                display: none !important;
            }

            /* ============ HIDE CONNECTOR DOTS AND SELECTION ============ */
            svg[width="3"] line[stroke-dasharray],
            svg[viewBox*="0 0 3"] line,
            circle[fill="#4caf50"],
            circle[fill="rgb(76, 175, 80)"] {
                display: none !important;
            }

            /* ============ HIDE SELECTED/ACTIVE STATES (NOT LABELS) ============ */
            [data-selected="true"]:not([data-flowi-label]):not(.flowi-label),
            [aria-selected="true"]:not([data-flowi-label]):not(.flowi-label),
            .selected:not([data-flowi-label]):not(.flowi-label),
            .active:not([data-flowi-label]):not(.flowi-label),
            .highlighted:not([data-flowi-label]):not(.flowi-label) {
                border: none !important;
                outline: none !important;
                box-shadow: none !important;
            }

            /* ============ FIX FOR POSITIONED ELEMENTS (NOT LABELS) ============ */
            [style*="position: absolute"] img:not([data-flowi-label] img):not(.flowi-label img),
            [style*="position: relative"] img:not([data-flowi-label] img):not(.flowi-label img) {
                border: none !important;
                outline: none !important;
            }

            /* ============ PRESERVE LABEL STYLING ============ */
            /* Labels already have borders defined - just don't remove them */
            /* No additional CSS needed - the exclusions above are sufficient */
             /* ============ FORCE CANVAS BACKGROUND TO MATCH THEME ============ */

             /* ============ MAKE CANVAS TRANSPARENT ============ */
            [data-testid="isometric-canvas"],
            [data-testid="isometric-canvas"] > div,
            .MuiBox-root[data-testid="isometric-canvas"] {
                background: transparent !important;
                background-color: transparent !important;
            }
        `;

        document.head.appendChild(styleEl);

        // Return cleanup function
        return () => {
            const style = document.getElementById('flowi-export-cleanup');
            if (style) {
                style.remove();
            }
        };
    }
}

export const createPNGExportHandler = () => new PNGExportHandler();
